/**
 * ApprovalTimeHistogram — admin-side application pipeline view.
 *
 * Rendering history:
 *   - Originally (PR (m.8) Phase 4): a Recharts horizontal bar chart
 *     bucketing time-to-decision in [<1d, 1-3d, 3-7d, 7-14d, 14d+]
 *     day buckets.
 *   - PR (z): rewritten as a single horizontal stacked bar of
 *     applicant counts by submission status.
 *   - PR (dd): rewritten again as a Recharts donut chart per
 *     spec-owner direction "make application bar chart a donut
 *     chart with application status as subcategories." The
 *     donut keeps the same submission-status subcategory
 *     vocabulary (Draft, Pending, Approved, Rejected) but
 *     trades the linear-bar visual encoding for a radial one
 *     with a center-readout total.
 *
 * Each ring segment uses its semantic color token, matching the
 * row-level pills in the Applications table:
 *   - Draft     → --muted (neutral cream)
 *   - Pending   → --destructive
 *   - Approved  → --success-muted
 *   - Rejected  → --destructive-muted
 *
 * The component name is preserved (`ApprovalTimeHistogram`) so
 * the call-site in `App.tsx`'s `AdminApplicationsPage` doesn't
 * need to change. The internal eyebrow + title still read
 * "Application Pipeline" / "{N} applicants for {year}".
 *
 * Spec: meridian-redesign — PR (m.8) Phase 4 (originally a
 * time-to-decision histogram; PR (z) reframed as stacked bar;
 * PR (dd) reframed as donut chart per spec-owner request).
 * Validates: Requirements 5.5, 14.1, 18.5
 */

import { useMemo, type JSX } from 'react';
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import type { ApplicantWithProfile } from './ApprovalQueue';
import { ChartCard } from '@/components/ui/chart-card';
import { useApplicantApprovalStatus } from '@/hooks/useApplicantApprovalStatus';
import { resolveActiveCompetitionId } from '@/lib/application-form/activeCompetition';
import { useActiveTenantId } from '@/context/AuthContext';

export interface ApprovalTimeHistogramProps {
  applicants: ApplicantWithProfile[];
  /** Competition year to compute pipeline for. */
  competitionYear: number;
}

interface PipelineSegment {
  /** Stable id used as React key + data-testid suffix. */
  id: 'draft' | 'submitted' | 'approved' | 'rejected';
  label: string;
  count: number;
  /** Tailwind text-color class for the count display in the legend. */
  textClass: string;
  /** CSS color (var()) used for the actual ring segment fill. */
  fill: string;
}

const SEGMENT_ORDER: ReadonlyArray<Pick<PipelineSegment, 'id' | 'label' | 'textClass' | 'fill'>> = [
  { id: 'draft',     label: 'Draft',    textClass: 'text-muted-foreground', fill: 'var(--muted-foreground)' },
  { id: 'submitted', label: 'Pending',  textClass: 'text-info-text',        fill: 'var(--info)'             },
  { id: 'approved',  label: 'Approved', textClass: 'text-success-text',     fill: 'var(--success)'          },
  { id: 'rejected',  label: 'Rejected', textClass: 'text-destructive-text', fill: 'var(--destructive)'      },
];

export function ApprovalTimeHistogram({
  applicants,
  competitionYear,
}: ApprovalTimeHistogramProps): JSX.Element {
  const activeTenantId = useActiveTenantId();
  // Approval status is sourced from the Approval_SSOT list read (keyed by
  // personId, joined onto the Applicant id) — NOT the legacy
  // `yearProfiles[].submissionStatus` roster (approval-status-ssot-unification
  // Req 4.6, 4.11). While the read is in flight or has failed we withhold the
  // counts entirely (Req 2.7, 2.8).
  const approval = useApplicantApprovalStatus(
    applicants,
    resolveActiveCompetitionId(competitionYear, activeTenantId),
  );

  const { segments, total } = useMemo(() => {
    const counts: Record<PipelineSegment['id'], number> = {
      draft: 0,
      submitted: 0,
      approved: 0,
      rejected: 0,
    };
    let totalApplicants = 0;

    if (approval.ready) {
      for (const applicant of applicants) {
        const status = approval.getStatus(applicant.id);
        // Absent `ApplicationResponse` for the competition ⇒ the applicant is
        // not part of this competition's pipeline (Req 2.3).
        if (!status) continue;
        totalApplicants += 1;
        switch (status) {
          case 'APPROVED':
            counts.approved += 1;
            break;
          case 'REJECTED':
            counts.rejected += 1;
            break;
          case 'SUBMITTED':
            counts.submitted += 1;
            break;
          case 'DRAFT':
          default:
            counts.draft += 1;
            break;
        }
      }
    }

    const segs: PipelineSegment[] = SEGMENT_ORDER.map((s) => ({
      ...s,
      count: counts[s.id],
    }));
    return { segments: segs, total: totalApplicants };
  }, [applicants, approval]);

  // Recharts <Pie> ignores zero-value slices but still emits a
  // legend entry for them, so we render zero-segments only in the
  // legend below — not in the donut data.
  const donutData = segments.filter((s) => s.count > 0);

  // Withhold counting on an Approval_SSOT read failure (Req 2.8) — present an
  // error state, never a stale/zeroed count as if authoritative.
  if (approval.error) {
    return (
      <ChartCard eyebrow="Application Pipeline" title="Application status unavailable">
        <div
          className="py-6 text-center text-eyebrow-sm text-destructive-text"
          role="alert"
          data-testid="approvals-pipeline-error"
        >
          Approval status could not be loaded. Counts are unavailable.
        </div>
      </ChartCard>
    );
  }

  // Withhold counting while the read/join is in flight (Req 2.7).
  if (!approval.ready) {
    return (
      <ChartCard eyebrow="Application Pipeline" title={`Loading applicants for ${competitionYear}…`}>
        <div
          className="py-6 text-center text-eyebrow-sm text-muted-foreground"
          data-testid="approvals-pipeline-loading"
        >
          Loading approval status…
        </div>
      </ChartCard>
    );
  }

  return (
    <ChartCard
      eyebrow="Application Pipeline"
      title={
        total > 0
          ? `${total} applicant${total === 1 ? '' : 's'} for ${competitionYear}`
          : `No applicants for ${competitionYear}`
      }
    >
      {total === 0 ? (
        <div className="py-6 text-center text-eyebrow-sm text-muted-foreground">
          No applications yet for this competition year.
        </div>
      ) : (
        <div
          data-testid="meridian-approvals-pipeline-donut"
          className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-6 items-center"
        >
          {/* Donut chart with center readout */}
          <div className="relative h-56 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={donutData}
                  dataKey="count"
                  nameKey="label"
                  innerRadius="62%"
                  outerRadius="92%"
                  paddingAngle={donutData.length > 1 ? 2 : 0}
                  startAngle={90}
                  endAngle={-270}
                  isAnimationActive={false}
                  stroke="var(--card)"
                  strokeWidth={2}
                >
                  {donutData.map((seg) => (
                    <Cell
                      key={seg.id}
                      fill={seg.fill}
                      data-testid={`pipeline-segment-${seg.id}`}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    background: 'var(--card)',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-input)',
                    fontSize: 12,
                    padding: '0.5rem 0.75rem',
                  }}
                  formatter={(value: number, name: string) => [
                    `${value} (${total > 0 ? Math.round((value / total) * 100) : 0}%)`,
                    name,
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
            {/* Center readout */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <span className="font-display text-3xl text-foreground italic leading-none">
                {total}
              </span>
              <span className="text-eyebrow-xs text-muted-foreground mt-1">
                Total
              </span>
            </div>
          </div>

          {/* Legend — count + label, no swatch, no percentages */}
          <div className="flex flex-col gap-3">
            {segments.map((seg) => (
              <div
                key={seg.id}
                data-testid={`pipeline-legend-${seg.id}`}
                className="flex items-baseline gap-3"
              >
                <span className={`font-display text-2xl italic tabular-nums leading-none ${seg.textClass}`}>
                  {seg.count}
                </span>
                <span className="text-eyebrow-sm text-muted-foreground">
                  {seg.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </ChartCard>
  );
}

export default ApprovalTimeHistogram;
